import React from 'react'

import { Chip, MenuItem, Switch, TextField, Typography } from '@mui/material'
import { REMOVE_OVERRIDE } from 'features/customers/utils/featureEdits'
import type { FeatureRow } from 'features/customers/utils/featureRows'
import { CONTACT_PROVIDER_LABELS } from 'features/customers/utils/features'

import { areLeadsUnlimited, EFeatureNames } from '@albacross/types'

import { Dash } from 'components/_base'

const PROVIDER_OPTIONS = Object.entries(CONTACT_PROVIDER_LABELS)

const NO_CONTACTS = 'None — no contacts'

// One width for every editable cell, or the column reads ragged.
const FIELD_WIDTH = 150

// The product shows the leads sentinel as Unlimited; printing the number disagrees with it.
export const isUnlimitedFeatureValue = (name: string | undefined, value: unknown) =>
  name === EFeatureNames.LeadsLimit && typeof value === 'number' && areLeadsUnlimited(value)

export const formatFeatureValue = (value: unknown, name?: string) => {
  // An empty provider is deliberate: it is how contacts get switched off.
  if (value === '' && name === EFeatureNames.ContactsProvider) return NO_CONTACTS
  if (value === undefined || value === null || value === '') return '—'
  if (isUnlimitedFeatureValue(name, value)) return 'Unlimited'
  if (typeof value === 'boolean') return value ? 'On' : 'Off'
  if (typeof value === 'number') return value.toLocaleString()
  if (typeof value === 'string') return CONTACT_PROVIDER_LABELS[value] ?? value

  return String(value)
}

type ReadOnlyProps = { value: unknown; muted?: boolean; name?: string }

export const FeatureValueText = ({ value, muted, name }: ReadOnlyProps) => {
  if (value === undefined || value === null || value === '') {
    return <Dash />
  }

  if (typeof value === 'boolean') {
    return (
      <Chip
        color={value ? 'success' : 'default'}
        label={value ? 'On' : 'Off'}
        size="small"
        sx={{ fontWeight: 700, letterSpacing: '0.03em', minWidth: 44 }}
        variant={muted ? 'outlined' : 'filled'}
      />
    )
  }

  return (
    <Typography color={muted ? 'text.secondary' : 'text.primary'} component="span" variant="body2">
      {formatFeatureValue(value, name)}
    </Typography>
  )
}

type Props = {
  row: FeatureRow
  edited: unknown
  onChange: (name: string, value: unknown) => void
}

const CustomerFeatureValue = ({ row, edited, onChange }: Props) => {
  const value = edited === undefined ? row.value : edited

  if (!row.editable) return <FeatureValueText name={row.name} value={row.value} />

  if (edited === REMOVE_OVERRIDE) return <FeatureValueText muted name={row.name} value={row.planValue} />

  if (row.kind === 'flag') {
    return (
      <Switch
        checked={!!value}
        onChange={event => onChange(row.name, event.target.checked)}
        size="small"
        slotProps={{ input: { 'aria-label': row.label } }}
      />
    )
  }

  if (row.kind === 'text') {
    return (
      <TextField
        onChange={event => onChange(row.name, event.target.value)}
        select
        size="small"
        slotProps={{ htmlInput: { 'aria-label': row.label }, select: { displayEmpty: true } }}
        sx={{ width: FIELD_WIDTH }}
        value={typeof value === 'string' ? value : ''}>
        {/* displayEmpty, or MUI renders a zero-width space instead of this option's label. */}
        <MenuItem value="">{NO_CONTACTS}</MenuItem>
        {PROVIDER_OPTIONS.map(([key, label]) => (
          <MenuItem key={key} value={key}>
            {label}
          </MenuItem>
        ))}
      </TextField>
    )
  }

  return (
    <TextField
      onChange={event => onChange(row.name, event.target.value)}
      placeholder={row.planValue === undefined ? 'No limit' : String(row.planValue)}
      size="small"
      slotProps={{ htmlInput: { 'aria-label': row.label, inputMode: 'numeric', min: 0 } }}
      sx={{ width: FIELD_WIDTH }}
      value={value === undefined || value === null ? '' : String(value)}
    />
  )
}

export default CustomerFeatureValue
